import { useEffect, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Chip, type ChipVariant } from '../../components/ui/Chip';
import {
  useCampaign,
  useCampaignStepStats,
  usePauseCampaign,
  useResumeCampaign,
  useUpdateCampaign,
  useUpdateCampaignMailboxes,
  useUpdateCampaignSmsNumber,
  type CampaignDetail,
  type CampaignStatus,
  type CampaignStepStat,
  type CampaignType,
} from '../../api/campaigns.api';
import { useToast } from '../../components/ui/Toast';
import { useConversations } from '../../api/inbox.api';
import { useSaveCampaignAsTemplate } from '../../api/campaign-templates.api';
import { useSmtpMailboxes, type SmtpMailbox } from '../../api/smtp-mailboxes.api';
import { useSmsAccounts } from '../../api/sms-accounts.api';
import { useOrgSettings } from '../../api/org-settings.api';
import { useOrgUsers } from '../../api/org-users.api';
import { findOwner } from '../../lib/people';
import { CampaignNoticeBanner, TaskErrorBanner } from './TaskErrorBanner';
import {
  useReportsSummary,
  useEngagement,
  useActivityFeed,
  useCampaignTimeline,
  type ReportSummary,
  type DailyEngagementPoint,
  type ActivityEventType,
  type MergedActivityItem,
} from '../../api/reports.api';

/* ── Screen 17 — Campaign detail · post-launch (Overview tab) ── */

/**
 * Live data is sourced from useCampaign(id) — see CampaignSummary below for
 * the shape mapped from CampaignDetail. The METRICS / ACTIVITY / CHART_DATA
 * blocks further down stay as placeholders for now; the metrics card surface
 * needs real activity_event aggregation which is a Reports-module job, not
 * the campaign-detail wiring (Section 5).
 */

interface CampaignSummary {
  id: string;
  name: string;
  typeLabel: string;
  status: CampaignStatus;
  statusVariant: ChipVariant;
  statusLabel: string;
  dayCount: number;
  startedOn: string;
  stepCount: number;
  stopRules: string;
  owner: { initials: string; name: string; color: string };
}

const TYPE_LABELS: Record<CampaignType, string> = {
  CANDIDATE: 'Candidate',
  BD: 'BD',
  JOB: 'Job',
  DEAL: 'Deal',
  EVENT: 'Event',
  NEWSLETTER: 'Newsletter',
  SMS_BLAST: 'SMS blast',
};

const STATUS_VARIANTS: Record<CampaignStatus, ChipVariant> = {
  ACTIVE: 'live',
  PAUSED: 'paused',
  COMPLETED: 'completed',
  DRAFT: 'draft',
  ARCHIVED: 'archived',
};

const STATUS_LABELS: Record<CampaignStatus, string> = {
  ACTIVE: 'Live',
  PAUSED: 'Paused',
  COMPLETED: 'Completed',
  DRAFT: 'Draft',
  ARCHIVED: 'Archived',
};

function buildSummary(c: CampaignDetail): CampaignSummary {
  const stopParts: string[] = [];
  if (c.sequence?.stop_on_reply) stopParts.push('reply');
  if (c.sequence?.stop_on_bounce) stopParts.push('bounce');
  if (c.sequence?.stop_on_booking) stopParts.push('booking');
  const stopRules = stopParts.length > 0 ? `Stops on ${stopParts.join(' · ')}` : 'No stop rules';

  const launched = c.launched_at ? new Date(c.launched_at) : null;
  const dayCount = launched
    ? Math.max(1, Math.floor((Date.now() - launched.getTime()) / (24 * 60 * 60 * 1000)) + 1)
    : 0;
  const startedOn = launched
    ? launched.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })
    : 'Not launched';

  return {
    id: c.id,
    name: c.name,
    typeLabel: TYPE_LABELS[c.type] ?? c.type,
    status: c.status,
    statusVariant: STATUS_VARIANTS[c.status] ?? 'idle',
    statusLabel: STATUS_LABELS[c.status] ?? c.status,
    dayCount,
    startedOn,
    stepCount: c.sequence?.steps?.length ?? 0,
    stopRules,
    // Owner display deferred to a follow-up — owner_user_id alone isn't
    // useful without a JT-user lookup which would belong in a Reports
    // hydration pass.
    owner: { initials: '··', name: c.owner_user_id, color: 'var(--color-brand-indigo)' },
  };
}

/* Metric, chart, activity, and sequence data are all live — see
 * useReportsSummary, useEngagement, useActivityFeed, useCampaignStepStats.
 * SequenceStepType is the lowercase visual key retained by TYPE_ACCENTS;
 * the API uses uppercase StepType which gets translated in the row renderer. */

type SequenceStepType = 'email' | 'wait' | 'branch' | 'task' | 'exit';


export function CampaignDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [tab, setTab] = useState<'overview' | 'audience' | 'activity' | 'settings'>('overview');
  const { data: campaign, isLoading, isError, error } = useCampaign(id);

  if (isLoading) {
    return <CenteredMessage>Loading campaign…</CenteredMessage>;
  }
  if (isError || !campaign) {
    const status = (error as { response?: { status?: number } } | undefined)?.response?.status;
    if (status === 404) return <CenteredMessage>Campaign not found.</CenteredMessage>;
    return <CenteredMessage>Couldn't load this campaign. Try again.</CenteredMessage>;
  }

  const summary = buildSummary(campaign);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
      <Breadcrumb name={summary.name} />
      {summary.status === 'DRAFT' && <DraftBanner campaignId={summary.id} />}
      <TaskErrorBanner taskError={campaign.task_error} taskErrorAt={campaign.task_error_at} />
      <CampaignNoticeBanner title="Texts are waiting." message={campaign.sms_notice} at={campaign.sms_notice_at} />
      <TitleRow summary={summary} />
      <TabBar tab={tab} onChange={setTab} />

      {tab === 'overview'  && <OverviewTab campaignId={summary.id} />}
      {tab === 'audience'  && <AudienceTab campaign={campaign} />}
      {tab === 'activity'  && <ActivityTab campaignId={summary.id} />}
      {tab === 'settings'  && <SettingsTab campaign={campaign} />}
    </div>
  );
}

function DraftBanner({ campaignId }: { campaignId: string }) {
  return (
    <div
      role="status"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 'var(--space-3)',
        padding: 'var(--space-3) var(--space-4)',
        borderRadius: 'var(--radius-input)',
        backgroundColor: 'var(--color-surface-muted)',
        border: '1px dashed var(--color-border-strong)',
        fontFamily: 'var(--font-sans)',
        fontSize: 'var(--text-sm)',
        color: 'var(--color-text-secondary)',
      }}
    >
      <span>
        This campaign is a <strong>draft</strong>, so it isn't sending yet. Finish setting it up in the builder and launch when ready.
      </span>
      <Link
        to={`/campaigns/${campaignId}/edit`}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          height: 'var(--btn-height-sm)',
          padding: '0 var(--btn-padding-x-sm)',
          borderRadius: 'var(--btn-radius)',
          backgroundColor: 'var(--color-action-primary)',
          color: 'var(--color-action-primary-text)',
          border: 'none',
          fontSize: 'var(--text-xs)',
          fontWeight: 'var(--font-weight-semibold)',
          textDecoration: 'none',
          whiteSpace: 'nowrap',
        }}
      >
        Continue in builder
      </Link>
    </div>
  );
}

function CenteredMessage({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        backgroundColor: 'var(--card-bg)',
        border: '1px solid var(--card-border)',
        borderRadius: 'var(--card-radius)',
        boxShadow: 'var(--card-shadow)',
        padding: 'var(--space-10)',
        textAlign: 'center',
        color: 'var(--color-text-muted)',
        fontFamily: 'var(--font-sans)',
        fontSize: 'var(--text-sm)',
      }}
    >
      {children}
    </div>
  );
}

/* ── Breadcrumb / title / tabs ── */

function Breadcrumb({ name }: { name: string }) {
  const items: { label: string; to: string | null }[] = [
    { label: 'Engage', to: '/dashboard' },
    { label: 'Campaigns', to: '/campaigns' },
    { label: name, to: null },
  ];
  return (
    <nav aria-label="Breadcrumb">
      <ol style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', listStyle: 'none', margin: 0, padding: 0, flexWrap: 'wrap' }}>
        {items.map((it, i) => (
          <li key={it.label} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', minWidth: 0 }}>
            {it.to ? (
              <a href={it.to} style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)', color: 'var(--color-text-muted)', textDecoration: 'none' }}>
                {it.label}
              </a>
            ) : (
              <span
                aria-current="page"
                style={{
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-weight-semibold)',
                  color: 'var(--color-text-secondary)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '520px',
                }}
              >
                {it.label}
              </span>
            )}
            {i < items.length - 1 && (
              <span aria-hidden="true" style={{ color: 'var(--color-text-subtle)' }}>
                <IconChevronRight size={12} />
              </span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

function TitleRow({ summary }: { summary: CampaignSummary }) {
  const { addToast } = useToast();
  const pause = usePauseCampaign();
  const resume = useResumeCampaign();
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);

  // Active campaigns can be paused; paused campaigns can be resumed. Anything
  // else (DRAFT, COMPLETED, ARCHIVED) hides the button entirely.
  const showPause = summary.status === 'ACTIVE';
  const showResume = summary.status === 'PAUSED';

  const handlePause = async () => {
    try {
      await pause.mutateAsync(summary.id);
      addToast({ variant: 'success', title: 'Campaign paused' });
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Try again in a moment.';
      addToast({ variant: 'danger', title: 'Could not pause', description: msg });
    }
  };

  const handleResume = async () => {
    try {
      await resume.mutateAsync(summary.id);
      addToast({ variant: 'success', title: 'Campaign resumed' });
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Try again in a moment.';
      addToast({ variant: 'danger', title: 'Could not resume', description: msg });
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--space-4)' }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
          <h1
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--text-4xl)',
              fontWeight: 'var(--font-weight-bold)',
              color: 'var(--color-text-brand)',
              letterSpacing: 'var(--tracking-tight)',
              lineHeight: 'var(--leading-tight)',
            }}
          >
            {summary.name}
          </h1>
          <TypePill>{summary.typeLabel}</TypePill>
          <Chip variant={summary.statusVariant}>{summary.statusLabel}</Chip>
          {summary.dayCount > 0 && <DayBadge day={summary.dayCount} />}
        </div>
        <p
          style={{
            marginTop: 'var(--space-2)',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-md)',
            color: 'var(--color-text-muted)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-3)',
            flexWrap: 'wrap',
          }}
        >
          <SubFact icon={<IconCalendar size={12} />}>Started {summary.startedOn}</SubFact>
          <Sep />
          <SubFact icon={<IconLayers size={12} />}>{summary.stepCount} steps</SubFact>
          <Sep />
          <SubFact icon={<IconStop size={12} />}>{summary.stopRules}</SubFact>
        </p>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexShrink: 0 }}>
        {showPause && (
          <SecondaryButton onClick={handlePause} disabled={pause.isPending}>
            <IconPause size={14} />
            <span style={{ marginLeft: 'var(--space-1)' }}>{pause.isPending ? 'Pausing…' : 'Pause'}</span>
          </SecondaryButton>
        )}
        {showResume && (
          <SecondaryButton onClick={handleResume} disabled={resume.isPending}>
            <IconPause size={14} />
            <span style={{ marginLeft: 'var(--space-1)' }}>{resume.isPending ? 'Resuming…' : 'Resume'}</span>
          </SecondaryButton>
        )}
        <Link
          to={`/campaigns/${summary.id}/workflow`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            height: 'var(--btn-height)',
            padding: '0 var(--btn-padding-x)',
            borderRadius: 'var(--btn-radius)',
            backgroundColor: 'var(--color-action-secondary)',
            color: 'var(--color-action-secondary-text)',
            border: '1px solid var(--color-action-secondary-border)',
            fontSize: 'var(--btn-font-size)',
            fontWeight: 'var(--btn-font-weight)',
            fontFamily: 'var(--font-sans)',
            textDecoration: 'none',
            whiteSpace: 'nowrap',
          }}
        >
          <IconWorkflow size={14} />
          <span style={{ marginLeft: 'var(--space-1)' }}>View workflow</span>
        </Link>
        <SecondaryButton onClick={() => setSaveTemplateOpen(true)}>
          <IconBookmark size={14} />
          <span style={{ marginLeft: 'var(--space-1)' }}>Save as template</span>
        </SecondaryButton>
        <Link
          to={`/campaigns/${summary.id}/report`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            height: 'var(--btn-height)',
            padding: '0 var(--btn-padding-x)',
            borderRadius: 'var(--btn-radius)',
            backgroundColor: 'var(--color-action-primary)',
            color: 'var(--color-action-primary-text)',
            border: 'none',
            fontSize: 'var(--btn-font-size)',
            fontWeight: 'var(--btn-font-weight)',
            fontFamily: 'var(--font-sans)',
            textDecoration: 'none',
            whiteSpace: 'nowrap',
          }}
        >
          <IconBarChart size={14} />
          <span style={{ marginLeft: 'var(--space-1)' }}>View report</span>
        </Link>
      </div>

      {saveTemplateOpen && (
        <SaveAsTemplateModal
          campaignId={summary.id}
          defaultName={summary.name}
          onClose={() => setSaveTemplateOpen(false)}
        />
      )}
    </div>
  );
}

function SaveAsTemplateModal({
  campaignId,
  defaultName,
  onClose,
}: {
  campaignId: string;
  defaultName: string;
  onClose: () => void;
}) {
  const { addToast } = useToast();
  const save = useSaveCampaignAsTemplate();
  const [name, setName] = useState(defaultName);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    setError(null);
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Give the template a name so you can find it later.');
      return;
    }
    try {
      await save.mutateAsync({ campaignId, name: trimmed });
      addToast({ variant: 'success', title: 'Template saved', description: trimmed });
      onClose();
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
        ?? (err as Error).message
        ?? 'Could not save template';
      setError(msg);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="save-tpl-title"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 'var(--z-modal)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'var(--modal-overlay-bg)',
        padding: 'var(--space-4)',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 480,
          backgroundColor: 'var(--card-bg)',
          borderRadius: 'var(--card-radius)',
          boxShadow: '0 24px 48px rgba(0,0,0,0.12)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <header style={{ padding: 'var(--space-5) var(--space-6)', borderBottom: '1px solid var(--color-border-default)' }}>
          <h2 id="save-tpl-title" style={{ margin: 0, fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xl)', fontWeight: 'var(--font-weight-bold)', color: 'var(--color-text-brand)' }}>
            Save as template
          </h2>
          <p style={{ marginTop: '2px', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
            Snapshot this campaign's type, sequence, and trigger config. Email step bodies are inlined so the template survives email-template edits or deletions.
          </p>
        </header>

        <div style={{ padding: 'var(--space-5) var(--space-6)', display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          <label
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-weight-semibold)',
              color: 'var(--color-text-secondary)',
            }}
          >
            Template name
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              maxLength={120}
              placeholder="e.g. Senior Backend · Sydney intro sequence"
              style={{
                marginTop: 'var(--space-1)',
                display: 'block',
                width: '100%',
                height: 'var(--input-height)',
                padding: '0 var(--space-3)',
                borderRadius: 'var(--input-radius)',
                border: '1px solid var(--color-border-default)',
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--input-font-size)',
                fontWeight: 'var(--font-weight-regular)',
                color: 'var(--color-text-primary)',
              }}
            />
          </label>

          {error && (
            <div
              role="alert"
              style={{
                padding: 'var(--space-2) var(--space-3)',
                borderRadius: 'var(--radius-input)',
                border: '1px solid var(--color-warning)',
                color: 'var(--color-warning)',
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--text-sm)',
              }}
            >
              {error}
            </div>
          )}
        </div>

        <footer style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-2)', padding: 'var(--space-4) var(--space-6)', borderTop: '1px solid var(--color-border-default)' }}>
          <SecondaryButton onClick={onClose} disabled={save.isPending}>
            Cancel
          </SecondaryButton>
          <PrimaryButton onClick={handleSave} disabled={save.isPending || !name.trim()}>
            {save.isPending ? 'Saving…' : 'Save template'}
          </PrimaryButton>
        </footer>
      </div>
    </div>
  );
}

function SubFact({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1)' }}>
      <span style={{ color: 'var(--color-text-subtle)', display: 'inline-flex' }}>{icon}</span>
      {children}
    </span>
  );
}

function Sep() {
  return <span aria-hidden="true" style={{ color: 'var(--color-text-subtle)' }}>·</span>;
}

function TypePill({ children }: { children: ReactNode }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--space-1)',
        height: 'var(--chip-height)',
        padding: 'var(--chip-padding-y) var(--chip-padding-x)',
        borderRadius: 'var(--chip-radius)',
        border: '1px solid var(--color-blue-100)',
        backgroundColor: 'var(--color-blue-50)',
        color: 'var(--color-brand-blue)',
        fontFamily: 'var(--font-sans)',
        fontSize: 'var(--chip-font-size)',
        fontWeight: 'var(--chip-font-weight)',
        letterSpacing: 'var(--tracking-wide)',
      }}
    >
      {children}
    </span>
  );
}

function DayBadge({ day }: { day: number }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 'var(--chip-height)',
        padding: 'var(--chip-padding-y) var(--chip-padding-x)',
        borderRadius: 'var(--chip-radius)',
        backgroundColor: 'var(--color-navy-50)',
        border: '1px solid var(--color-navy-100)',
        color: 'var(--color-text-brand)',
        fontFamily: 'var(--font-mono)',
        fontSize: 'var(--chip-font-size)',
        fontWeight: 'var(--chip-font-weight)',
        letterSpacing: 'var(--tracking-wide)',
      }}
    >
      Day {day}
    </span>
  );
}

function TabBar({
  tab,
  onChange,
}: {
  tab: 'overview' | 'audience' | 'activity' | 'settings';
  onChange: (t: 'overview' | 'audience' | 'activity' | 'settings') => void;
}) {
  const tabs: { id: typeof tab; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'audience', label: 'Audience' },
    { id: 'activity', label: 'Activity' },
    { id: 'settings', label: 'Settings' },
  ];
  return (
    <div role="tablist" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', borderBottom: '1px solid var(--color-border-default)' }}>
      {tabs.map((t) => {
        const isActive = t.id === tab;
        return (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(t.id)}
            style={{
              height: 'var(--btn-height-lg)',
              padding: '0 var(--space-4)',
              backgroundColor: 'transparent',
              border: 'none',
              borderBottom: `2px solid ${isActive ? 'var(--color-action-primary)' : 'transparent'}`,
              marginBottom: '-1px',
              color: isActive ? 'var(--color-text-brand)' : 'var(--color-text-muted)',
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--text-sm)',
              fontWeight: isActive ? 'var(--font-weight-semibold)' : 'var(--font-weight-medium)',
              cursor: 'pointer',
              transition: 'color var(--transition-base), border-color var(--transition-base)',
            }}
            onMouseEnter={(e) => {
              if (!isActive) e.currentTarget.style.color = 'var(--color-text-primary)';
            }}
            onMouseLeave={(e) => {
              if (!isActive) e.currentTarget.style.color = 'var(--color-text-muted)';
            }}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

/* ── Overview tab ── */

function OverviewTab({ campaignId }: { campaignId: string }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)', gap: 'var(--space-6)', alignItems: 'flex-start' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)', minWidth: 0 }}>
        <MetricsRow campaignId={campaignId} />
        <SequenceReadOnly campaignId={campaignId} />
        <EngagementChartCard campaignId={campaignId} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)', minWidth: 0 }}>
        <ActivityFeedPanel campaignId={campaignId} />
        <InboxShortcut campaignId={campaignId} />
      </div>
    </div>
  );
}

/* ── Audience tab ── */

function AudienceTab({ campaign }: { campaign: CampaignDetail }) {
  const audience = campaign.audience;
  return (
    <section
      style={{
        backgroundColor: 'var(--card-bg)',
        border: '1px solid var(--card-border)',
        borderRadius: 'var(--card-radius)',
        boxShadow: 'var(--card-shadow)',
        padding: 'var(--card-padding)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-4)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <h2 style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-lg)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-brand)', letterSpacing: 'var(--tracking-tight)', margin: 0 }}>
          Audience
        </h2>
        {audience && (
          <Link
            to={`/audiences?selected=${audience.id}`}
            style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-link)', textDecoration: 'none' }}
          >
            Edit audience →
          </Link>
        )}
      </div>

      {!audience ? (
        <p style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
          No audience attached to this campaign yet.
        </p>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 'var(--space-4)' }}>
          <DetailStat label="Audience" value={audience.name} />
          <DetailStat label="Members" value={audience.member_count.toLocaleString()} />
          <DetailStat label="Refresh" value={audience.is_dynamic ? 'Dynamic' : 'Static snapshot'} />
        </div>
      )}

      <p style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', lineHeight: 'var(--leading-relaxed)' }}>
        {audience?.is_dynamic
          ? 'Dynamic audiences re-evaluate against JT data on each sync. New matches join the pool automatically.'
          : 'Static audiences enrol a one-time snapshot at launch. New JT records matching the rules will NOT be auto-enrolled.'}
      </p>
    </section>
  );
}

function DetailStat({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)' }}>
        {label}
      </span>
      <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-md)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)' }}>
        {value}
      </span>
    </div>
  );
}

/* ── Activity tab ── */

function ActivityTab({ campaignId }: { campaignId: string }) {
  // Merged timeline: activity_events (recipient actions) + audit_log_entries
  // (actor actions on the campaign) interleaved. Polls once a minute.
  const { data, isLoading } = useCampaignTimeline(campaignId, { limit: 100 });
  const items = data ?? [];

  return (
    <section
      style={{
        backgroundColor: 'var(--card-bg)',
        border: '1px solid var(--card-border)',
        borderRadius: 'var(--card-radius)',
        boxShadow: 'var(--card-shadow)',
        overflow: 'hidden',
      }}
    >
      <header style={{ padding: 'var(--space-4) var(--space-5)', borderBottom: '1px solid var(--color-border-default)' }}>
        <h2 style={{ margin: 0, fontFamily: 'var(--font-sans)', fontSize: 'var(--text-lg)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-brand)', letterSpacing: 'var(--tracking-tight)' }}>
          Activity
        </h2>
        <p style={{ marginTop: '2px', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
          Recipient events (sent/opened/clicked/replied) interleaved with actor
          actions (launch, pause, settings): newest first. Refreshes every minute.
        </p>
      </header>

      {isLoading && items.length === 0 ? (
        <div style={{ padding: 'var(--space-5)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
          Loading activity…
        </div>
      ) : items.length === 0 ? (
        <div style={{ padding: 'var(--space-5)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
          No activity yet. Once sends go out, events will appear here.
        </div>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {items.map((item, i) => (
            <ActivityRow key={item.id} item={item} firstRow={i === 0} />
          ))}
        </ul>
      )}
    </section>
  );
}

function describeAuditAction(action: string, actorEmail: string | null): string {
  const who = actorEmail ?? 'system';
  switch (action) {
    case 'campaign.launch':   return `${who} launched the campaign`;
    case 'campaign.pause':    return `${who} paused the campaign`;
    case 'campaign.resume':   return `${who} resumed the campaign`;
    case 'campaign.archive':  return `${who} archived the campaign`;
    case 'campaign.create':   return `${who} created the campaign`;
    case 'campaign.update':   return `${who} updated the campaign`;
    case 'sequence.update':   return `${who} edited the sequence`;
    case 'campaign.settings_update': return `${who} updated settings`;
    case 'campaign.audience_update': return `${who} changed the audience`;
    case 'campaign.mailboxes_update': return `${who} changed the mailbox selection`;
    default:                  return `${who} · ${action}`;
  }
}

function ActivityRow({ item, firstRow }: { item: MergedActivityItem; firstRow: boolean }) {
  const isAudit = item.source === 'audit';
  const kindLabel = isAudit ? 'AUDIT' : item.kind;
  const description = isAudit
    ? describeAuditAction(item.kind, item.actor_email)
    : item.contact_email ?? '-';
  return (
    <li
      style={{
        padding: 'var(--space-3) var(--space-5)',
        borderTop: firstRow ? 'none' : '1px solid var(--color-border-default)',
        display: 'grid',
        gridTemplateColumns: '110px 1fr auto',
        gap: 'var(--space-3)',
        alignItems: 'baseline',
        backgroundColor: isAudit ? 'var(--color-surface-subtle, transparent)' : 'transparent',
      }}
    >
      <span
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 'var(--text-xs)',
          fontWeight: 'var(--font-weight-bold)',
          color: isAudit ? 'var(--color-text-brand)' : 'var(--color-text-secondary)',
          letterSpacing: 'var(--tracking-caps)',
        }}
      >
        {kindLabel}
      </span>
      <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {description}
      </span>
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>
        {new Date(item.occurred_at).toLocaleString()}
      </span>
    </li>
  );
}

/* ── Settings tab ── */

function SettingsTab({ campaign }: { campaign: CampaignDetail }) {
  const isDraft = campaign.status === 'DRAFT';
  const triggerLabel = !campaign.trigger_config
    ? 'On launch (no auto-enrollment)'
    : campaign.trigger_config.type === 'stage_change' ? 'Stage change'
      : campaign.trigger_config.type === 'list_added' ? 'Added to a list'
        : campaign.trigger_config.type === 'new_placement' ? 'New placement'
          : campaign.trigger_config.type;

  return (
    <section
      style={{
        backgroundColor: 'var(--card-bg)',
        border: '1px solid var(--card-border)',
        borderRadius: 'var(--card-radius)',
        boxShadow: 'var(--card-shadow)',
        padding: 'var(--card-padding)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-5)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--space-4)' }}>
        <div>
          <h2 style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-lg)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-brand)', letterSpacing: 'var(--tracking-tight)', margin: 0 }}>
            Settings
          </h2>
          <p style={{ marginTop: '2px', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
            {isDraft
              ? 'Edits while the campaign is a draft. Once launched, settings become read-only. Pause first to change them.'
              : `Campaign is ${campaign.status.toLowerCase()}, so settings are read-only. ${campaign.status === 'ACTIVE' ? 'Pause the campaign to edit basics.' : ''}`}
          </p>
        </div>
        {isDraft && (
          <Link
            to={`/campaigns/${campaign.id}/edit`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              height: 'var(--btn-height)',
              padding: '0 var(--btn-padding-x)',
              borderRadius: 'var(--btn-radius)',
              backgroundColor: 'var(--color-action-primary)',
              color: 'var(--color-action-primary-text)',
              border: 'none',
              fontSize: 'var(--btn-font-size)',
              fontWeight: 'var(--btn-font-weight)',
              fontFamily: 'var(--font-sans)',
              textDecoration: 'none',
              whiteSpace: 'nowrap',
            }}
          >
            Edit in builder
          </Link>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 'var(--space-4)' }}>
        <DetailStat label="Name" value={campaign.name} />
        <DetailStat label="Type" value={campaign.type} />
        <DetailStat label="Status" value={campaign.status} />
        <DetailStat label="Trigger" value={triggerLabel} />
      </div>

      {campaign.description && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)' }}>
            Description
          </span>
          <p style={{ margin: 0, fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-primary)', lineHeight: 'var(--leading-relaxed)' }}>
            {campaign.description}
          </p>
        </div>
      )}

      {campaign.sequence && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          <h3 style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-md)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-secondary)', margin: 0 }}>
            Sequence rules
          </h3>
          <ul style={{ margin: 0, paddingLeft: 'var(--space-5)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-primary)', lineHeight: 'var(--leading-relaxed)' }}>
            <li>Max {campaign.sequence.max_steps} steps</li>
            <li>{campaign.sequence.stop_on_reply ? 'Stops on reply' : 'Continues even after a reply'}</li>
            <li>{campaign.sequence.stop_on_bounce ? 'Stops on bounce' : 'Continues even after a bounce'}</li>
            <li>{campaign.sequence.stop_on_booking ? 'Stops on meeting booked' : 'Continues even after a booking'}</li>
          </ul>
        </div>
      )}

      <OwnerSelector campaign={campaign} />

      {campaign.type !== 'NEWSLETTER' && campaign.type !== 'SMS_BLAST' && <MailboxSelector campaign={campaign} />}

      {campaign.type !== 'NEWSLETTER' && <SmsNumberSelector campaign={campaign} />}
    </section>
  );
}

/**
 * Bind an SMS sender number to the campaign post-launch. One number per
 * campaign; every SMS step routes through it.
 */
function SmsNumberSelector({ campaign }: { campaign: CampaignDetail }) {
  const { data, isLoading } = useSmsAccounts();
  const { data: orgSettings } = useOrgSettings();
  const accounts = data?.data ?? [];
  // Per-campaign binding is Twilio-only — only Twilio numbers are pickable here.
  const numbers = accounts
    .filter((a) => a.provider === 'TWILIO')
    .flatMap((a) => a.numbers.map((n) => ({
      ...n,
      account_label: a.label,
      account_status: a.status,
    })));
  const [value, setValue] = useState<string>(campaign.sms_number_id ?? '');
  const update = useUpdateCampaignSmsNumber(campaign.id);
  const { addToast } = useToast();

  const dirty = (value || null) !== (campaign.sms_number_id ?? null);

  // Under Dialpad the sender is the campaign owner's linked number (resolved at
  // send time) — there is no per-campaign number to pick, so show a note instead.
  if (orgSettings?.data?.active_sms_provider === 'DIALPAD') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
        <h3 style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-md)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-secondary)', margin: 0 }}>
          SMS sender number
        </h3>
        <p style={{ margin: 0, fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', lineHeight: 'var(--leading-relaxed)' }}>
          Your org sends SMS via <strong>Dialpad</strong>, so this campaign sends from the
          campaign owner's Dialpad number, as linked in JobTetris. There's no per-campaign number to pick.
          The default sender is under <Link to="/settings" style={{ color: 'var(--color-text-link)', textDecoration: 'none' }}>Settings → SMS</Link>.
        </p>
      </div>
    );
  }

  const save = () => {
    update.mutate(
      { sms_number_id: value === '' ? null : value },
      {
        onSuccess: () => addToast({
          variant: 'success',
          title: 'SMS number updated',
          description: value === '' ? 'No SMS number bound.' : `Bound to ${numbers.find((n) => n.id === value)?.e164 ?? value}.`,
        }),
        onError: (err) => addToast({ variant: 'danger', title: 'Could not update SMS number', description: (err as Error).message }),
      }
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
      <h3 style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-md)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-secondary)', margin: 0 }}>
        SMS sender number
      </h3>
      <p style={{ margin: 0, fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', lineHeight: 'var(--leading-relaxed)' }}>
        One number per campaign. Required when the sequence includes SMS steps;
        leave unset for email-only campaigns.
      </p>

      {isLoading ? (
        <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
          Loading numbers…
        </span>
      ) : numbers.length === 0 ? (
        <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
          No Twilio numbers configured yet. Connect a Twilio account in Settings.
        </span>
      ) : (
        <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
          <select
            value={value}
            onChange={(e) => setValue(e.target.value)}
            style={{
              height: 'var(--btn-height)',
              padding: '0 var(--space-3)',
              borderRadius: 'var(--radius-input)',
              border: '1px solid var(--color-border-default)',
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--text-sm)',
              backgroundColor: 'var(--color-surface-white)',
              minWidth: '280px',
            }}
          >
            <option value="">No SMS number</option>
            {numbers.map((n) => (
              <option key={n.id} value={n.id} disabled={n.account_status === 'DISABLED'}>
                {n.e164} · {n.account_label} ({n.country_code})
                {n.account_status === 'DISABLED' ? ' · disabled' : ''}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={save}
            disabled={!dirty || update.isPending}
            style={{
              height: 'var(--btn-height)',
              padding: '0 var(--btn-padding-x)',
              borderRadius: 'var(--btn-radius)',
              backgroundColor: dirty ? 'var(--color-action-primary)' : 'var(--color-surface-subtle, var(--color-surface-muted))',
              color: dirty ? 'var(--color-action-primary-text)' : 'var(--color-text-muted)',
              border: 'none',
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--btn-font-size)',
              fontWeight: 'var(--btn-font-weight)',
              cursor: dirty && !update.isPending ? 'pointer' : 'not-allowed',
            }}
          >
            {update.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Re-assign campaign owner. Allowed at any status — only affects future
 * mailbox-picker bias (the owner's personal mailboxes come first when no
 * allowlist is set) and the activity-tab actor on subsequent actions.
 */
function OwnerSelector({ campaign }: { campaign: CampaignDetail }) {
  const { data: users, isLoading } = useOrgUsers();
  const update = useUpdateCampaign(campaign.id);
  const { addToast } = useToast();
  // The owner id may be the user's JobTetris account id if they hadn't signed in yet (JT1-1908),
  // so the picker works on the matching user's jt_user_id.
  const currentMatch = findOwner(campaign.owner_user_id, users);
  const currentValue = currentMatch?.jt_user_id ?? campaign.owner_user_id;
  const [value, setValue] = useState(currentValue);
  useEffect(() => { setValue(currentValue); }, [currentValue]);

  const dirty = value !== currentValue;

  const save = async () => {
    try {
      await update.mutateAsync({ owner_user_id: value });
      addToast({ variant: 'success', title: 'Campaign owner updated' });
    } catch (err) {
      addToast({ variant: 'danger', title: 'Could not update owner', description: (err as Error).message });
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
      <h3 style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-md)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-secondary)', margin: 0 }}>
        Owner
      </h3>
      <p style={{ margin: 0, fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', lineHeight: 'var(--leading-relaxed)' }}>
        Owns the campaign for picker bias + activity-feed attribution. Re-assignment
        is allowed at any time and doesn&rsquo;t affect past sends.
      </p>
      <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
        {isLoading ? (
          <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>Loading users…</span>
        ) : (
          <select
            value={value}
            onChange={(e) => setValue(e.target.value)}
            style={{
              height: 'var(--btn-height)',
              padding: '0 var(--space-3)',
              borderRadius: 'var(--radius-input)',
              border: '1px solid var(--color-border-default)',
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--text-sm)',
              backgroundColor: 'var(--color-surface-white)',
              minWidth: '280px',
            }}
          >
            {!currentMatch && <option value={campaign.owner_user_id}>Someone no longer in the team</option>}
            {users?.map((u) => (
              <option key={u.jt_user_id} value={u.jt_user_id}>
                {u.name} · {u.email}
              </option>
            ))}
          </select>
        )}
        <button
          type="button"
          onClick={save}
          disabled={!dirty || update.isPending}
          style={{
            height: 'var(--btn-height)',
            padding: '0 var(--btn-padding-x)',
            borderRadius: 'var(--btn-radius)',
            backgroundColor: dirty ? 'var(--color-action-primary)' : 'var(--color-surface-subtle, var(--color-surface-muted))',
            color: dirty ? 'var(--color-action-primary-text)' : 'var(--color-text-muted)',
            border: 'none',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--btn-font-size)',
            fontWeight: 'var(--btn-font-weight)',
            cursor: dirty && !update.isPending ? 'pointer' : 'not-allowed',
          }}
        >
          {update.isPending ? 'Saving…' : 'Save owner'}
        </button>
      </div>
    </div>
  );
}

/**
 * Per-campaign outreach mailbox allowlist. Empty selection = no restriction
 * (any of the owner's eligible mailboxes). Editable at any campaign status —
 * future sends pick up the change; past sends keep their sticky binding.
 */
function MailboxSelector({ campaign }: { campaign: CampaignDetail }) {
  const { data, isLoading } = useSmtpMailboxes();
  const allMailboxes = data?.data ?? [];
  const initial = (campaign.allowed_mailbox_ids ?? []).filter((id) =>
    allMailboxes.some((m) => m.id === id)
  );
  const [selected, setSelected] = useState<string[]>(initial);
  const [savedSnapshot, setSavedSnapshot] = useState<string[]>(initial);
  const { addToast } = useToast();
  const mutation = useUpdateCampaignMailboxes(campaign.id);

  const dirty = !sameSet(selected, savedSnapshot);
  const isRestricted = selected.length > 0;
  const labelFor = (m: SmtpMailbox) =>
    m.is_shared ? `${m.label} · shared` : m.label;

  const toggle = (id: string) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const save = () => {
    mutation.mutate(
      { allowed_mailbox_ids: selected.length === 0 ? null : selected },
      {
        onSuccess: () => {
          setSavedSnapshot(selected);
          addToast({
            variant: 'success',
            title: 'Mailbox selection updated',
            description:
              selected.length === 0
                ? 'No restriction: all eligible mailboxes can send.'
                : `Restricted to ${selected.length} mailbox${selected.length === 1 ? '' : 'es'}.`,
          });
        },
        onError: (err) =>
          addToast({
            variant: 'danger',
            title: 'Could not update mailbox selection',
            description: (err as Error).message,
          }),
      }
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 'var(--space-3)' }}>
        <h3 style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-md)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-secondary)', margin: 0 }}>
          Outreach mailboxes
        </h3>
        <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
          {isRestricted
            ? `Restricted to ${selected.length} mailbox${selected.length === 1 ? '' : 'es'} · pool routing`
            : 'No restriction: picker uses default ownership order'}
        </span>
      </div>
      <p style={{ margin: 0, fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', lineHeight: 'var(--leading-relaxed)' }}>
        Pick one mailbox to lock the sender, multiple to use a pool, or leave
        unselected for default. Once a recipient receives their first send from
        a mailbox, subsequent steps for that recipient stick to the same one.
      </p>

      {isLoading ? (
        <div style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
          Loading mailboxes…
        </div>
      ) : allMailboxes.length === 0 ? (
        <div style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
          No SMTP mailboxes configured yet. Add one in Settings → Mailboxes.
        </div>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
          {allMailboxes.map((m) => {
            const checked = selected.includes(m.id);
            return (
              <li key={m.id}>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)',
                    padding: 'var(--space-2)',
                    borderRadius: 'var(--btn-radius)',
                    backgroundColor: checked ? 'var(--color-surface-subtle, transparent)' : 'transparent',
                    cursor: 'pointer',
                  }}
                >
                  <input type="checkbox" checked={checked} onChange={() => toggle(m.id)} />
                  <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-primary)' }}>
                    {labelFor(m)}
                  </span>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                    {m.email_address}
                  </span>
                  {m.status !== 'ACTIVE' && (
                    <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                      ({m.status.toLowerCase()})
                    </span>
                  )}
                </label>
              </li>
            );
          })}
        </ul>
      )}

      <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
        <button
          type="button"
          onClick={save}
          disabled={!dirty || mutation.isPending}
          style={{
            height: 'var(--btn-height)',
            padding: '0 var(--btn-padding-x)',
            borderRadius: 'var(--btn-radius)',
            backgroundColor: dirty ? 'var(--color-action-primary)' : 'var(--color-action-disabled, var(--color-surface-subtle))',
            color: dirty ? 'var(--color-action-primary-text)' : 'var(--color-text-muted)',
            border: 'none',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--btn-font-size)',
            fontWeight: 'var(--btn-font-weight)',
            cursor: dirty && !mutation.isPending ? 'pointer' : 'not-allowed',
          }}
        >
          {mutation.isPending ? 'Saving…' : 'Save mailbox selection'}
        </button>
        {dirty && (
          <button
            type="button"
            onClick={() => setSelected(savedSnapshot)}
            disabled={mutation.isPending}
            style={{
              height: 'var(--btn-height)',
              padding: '0 var(--btn-padding-x)',
              borderRadius: 'var(--btn-radius)',
              backgroundColor: 'transparent',
              color: 'var(--color-text-secondary)',
              border: '1px solid var(--color-border-default)',
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--btn-font-size)',
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}

function sameSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((v, i) => v === sortedB[i]);
}

/* ── Metric cards (live) ── */

interface MetricCardModel {
  label: string;
  value: string;
  sub: string;
}

function MetricsRow({ campaignId }: { campaignId: string }) {
  const { data, isLoading } = useReportsSummary({ campaign_id: campaignId });
  const cards = data ? metricsFromSummary(data) : null;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 'var(--space-4)' }}>
      {(cards ?? placeholderMetrics()).map((m) => (
        <MetricCard key={m.label} metric={m} loading={isLoading && !data} />
      ))}
    </div>
  );
}

function metricsFromSummary(s: ReportSummary): MetricCardModel[] {
  return [
    { label: 'Sent', value: s.sent.toLocaleString(), sub: `${s.delivered.toLocaleString()} delivered` },
    { label: 'Open rate', value: formatRate(s.open_rate), sub: `${s.opened.toLocaleString()} opens` },
    { label: 'Reply rate', value: formatRate(s.reply_rate), sub: `${s.replied.toLocaleString()} replies` },
    { label: 'Bounces', value: s.bounced.toLocaleString(), sub: formatRate(s.bounce_rate) + ' bounce rate' },
  ];
}

function placeholderMetrics(): MetricCardModel[] {
  return [
    { label: 'Sent', value: '-', sub: '' },
    { label: 'Open rate', value: '-', sub: '' },
    { label: 'Reply rate', value: '-', sub: '' },
    { label: 'Bounces', value: '-', sub: '' },
  ];
}

function formatRate(r: number): string {
  return `${(r * 100).toFixed(1)}%`;
}

function MetricCard({ metric, loading }: { metric: MetricCardModel; loading?: boolean }) {
  return (
    <div
      style={{
        backgroundColor: 'var(--card-bg)',
        border: '1px solid var(--card-border)',
        borderRadius: 'var(--card-radius)',
        boxShadow: 'var(--card-shadow)',
        padding: 'var(--stat-card-padding)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-2)',
        opacity: loading ? 0.55 : 1,
      }}
    >
      <span
        style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--text-xs)',
          fontWeight: 'var(--font-weight-semibold)',
          color: 'var(--color-text-muted)',
          letterSpacing: 'var(--tracking-caps)',
          textTransform: 'uppercase',
        }}
      >
        {metric.label}
      </span>
      <span
        style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--stat-card-number-size)',
          fontWeight: 'var(--stat-card-number-weight)',
          color: 'var(--color-text-brand)',
          letterSpacing: 'var(--tracking-tight)',
          lineHeight: 'var(--leading-tight)',
        }}
      >
        {metric.value}
      </span>
      <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
        {metric.sub || ' '}
      </span>
      <span style={{ height: '14px' }} />
    </div>
  );
}

/* ── Sequence read-only list ── */

function SequenceReadOnly({ campaignId }: { campaignId: string }) {
  const { data: stats, isLoading } = useCampaignStepStats(campaignId);
  const rows = stats ?? [];

  return (
    <section
      style={{
        backgroundColor: 'var(--card-bg)',
        border: '1px solid var(--card-border)',
        borderRadius: 'var(--card-radius)',
        boxShadow: 'var(--card-shadow)',
        overflow: 'hidden',
      }}
    >
      <PanelHeader
        title="Sequence"
        linkLabel="Edit sequence"
        linkTo={`/campaigns/${campaignId}/edit/sequence`}
      />

      {isLoading && rows.length === 0 && (
        <div style={{ padding: 'var(--space-5)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
          Loading sequence…
        </div>
      )}

      {!isLoading && rows.length === 0 && (
        <div style={{ padding: 'var(--space-5)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
          No steps configured yet.
        </div>
      )}

      {rows.length > 0 && (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {rows.map((step, i) => {
            const nextStep = rows[i + 1];
            // Inter-step DELAY indicator: the visual "wait between steps"
            // hint shown in the original mock. We surface it for any step
            // whose successor is a DELAY (rather than re-showing the DELAY
            // row itself with no data of its own).
            const interDelayDays = step.type !== 'DELAY' && nextStep?.type === 'DELAY' ? nextStep.delay_days : null;
            return (
              <li key={step.step_number}>
                <SequenceReadOnlyRow step={step} />
                {interDelayDays != null && i < rows.length - 1 && (
                  <div
                    style={{
                      padding: '0 var(--space-5)',
                      borderTop: '1px solid var(--color-border-default)',
                    }}
                  >
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 'var(--space-1)',
                        padding: 'var(--space-1) 0',
                        fontFamily: 'var(--font-sans)',
                        fontSize: 'var(--text-xs)',
                        color: 'var(--color-text-muted)',
                      }}
                    >
                      <IconClock size={11} />
                      Wait {interDelayDays} business {interDelayDays === 1 ? 'day' : 'days'}
                    </span>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

const STEP_TYPE_LABEL: Record<SequenceStepType, string> = {
  email: 'Email',
  wait: 'Wait',
  branch: 'Branch',
  task: 'Task',
  exit: 'Exit',
};

interface TypeAccent {
  bg: string;
  fg: string;
  border: string;
}

const TYPE_ACCENTS: Record<SequenceStepType, TypeAccent> = {
  email:  { bg: 'var(--color-blue-50)',     fg: 'var(--color-brand-blue)',   border: 'var(--color-blue-100)' },
  wait:   { bg: 'var(--color-surface-muted)', fg: 'var(--color-text-muted)', border: 'var(--color-border-default)' },
  branch: { bg: 'var(--color-indigo-50)',   fg: 'var(--color-brand-indigo)', border: 'var(--color-indigo-100)' },
  task:   { bg: 'var(--color-warning-bg)',  fg: 'var(--color-warning)',      border: 'var(--color-warning-border)' },
  exit:   { bg: 'var(--color-navy-50)',     fg: 'var(--color-brand-navy)',   border: 'var(--color-navy-100)' },
};

function SequenceReadOnlyRow({ step }: { step: CampaignStepStat }) {
  // Map the API's uppercase type → the visual accent dictionary. We keep
  // the existing lowercase keys in TYPE_ACCENTS for backwards compatibility
  // with the rest of the page; the lookup is a thin translator.
  const visualType: SequenceStepType =
    step.type === 'EMAIL' ? 'email'
    : step.type === 'DELAY' ? 'wait'
    : step.type === 'BRANCH' ? 'branch'
    : step.type === 'TASK' ? 'task'
    : 'exit';
  const accent = TYPE_ACCENTS[visualType];

  const isEmail = step.type === 'EMAIL';
  const isActivelySending = isEmail && step.in_queue > 0;

  const label = step.label ?? defaultLabelForType(step.type);
  const description = describeStepStat(step);

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'auto 1fr auto',
        alignItems: 'center',
        gap: 'var(--space-3)',
        padding: 'var(--space-3) var(--space-5)',
        borderTop: step.step_number === 1 ? 'none' : '1px solid var(--color-border-default)',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 'var(--space-9)',
          height: 'var(--space-9)',
          borderRadius: 'var(--radius-input)',
          backgroundColor: accent.bg,
          border: `1px solid ${accent.border}`,
          color: accent.fg,
          flexShrink: 0,
        }}
      >
        {visualType === 'email' && <IconMail size={16} />}
        {visualType === 'wait' && <IconClock size={16} />}
        {visualType === 'branch' && <IconBranch size={16} />}
        {visualType === 'task' && <IconCheckSquare size={16} />}
        {visualType === 'exit' && <IconLogOut size={16} />}
      </span>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-2)' }}>
          <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-bold)', color: accent.fg, textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)' }}>
            {STEP_TYPE_LABEL[visualType]}
          </span>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
            Step {step.step_number}
          </span>
        </div>
        <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-md)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)' }}>
          {label}
        </span>
        <span
          style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-xs)',
            color: 'var(--color-text-muted)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {description}
        </span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        {isActivelySending && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--space-1)',
              height: 'var(--chip-height)',
              padding: 'var(--chip-padding-y) var(--chip-padding-x)',
              borderRadius: 'var(--chip-radius)',
              border: '1px solid var(--chip-inprogress-border)',
              backgroundColor: 'var(--chip-inprogress-bg)',
              color: 'var(--chip-inprogress-text)',
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--chip-font-size)',
              fontWeight: 'var(--chip-font-weight)',
              letterSpacing: 'var(--tracking-wide)',
            }}
          >
            <span aria-hidden="true" style={{ width: '6px', height: '6px', borderRadius: 'var(--radius-full)', backgroundColor: 'var(--color-brand-blue)' }} />
            Sending
          </span>
        )}
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'baseline',
            gap: 'var(--space-1)',
            fontFamily: 'var(--font-sans)',
          }}
          title={
            isEmail
              ? `${step.in_queue} in queue · ${step.sent} sent`
              : `${step.in_queue} in queue`
          }
        >
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-md)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)' }}>
            {step.in_queue}
          </span>
          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>in step</span>
        </span>
      </div>
    </div>
  );
}

function defaultLabelForType(type: CampaignStepStat['type']): string {
  switch (type) {
    case 'EMAIL': return '(no subject)';
    case 'SMS': return '(no SMS body)';
    case 'DELAY': return 'Wait';
    case 'BRANCH': return 'Branch';
    case 'TASK': return 'Recruiter task';
    case 'EXIT': return 'End sequence';
  }
}

function describeStepStat(step: CampaignStepStat): string {
  if (step.type === 'EMAIL') {
    return `${step.sent.toLocaleString()} sent · ${step.completed.toLocaleString()} past this step`;
  }
  if (step.type === 'DELAY') {
    return `Wait ${step.delay_days ?? 0} business ${step.delay_days === 1 ? 'day' : 'days'}`;
  }
  if (step.type === 'BRANCH') return 'Routes based on engagement';
  if (step.type === 'TASK') return 'Creates a JT task on the candidate';
  return 'End of sequence';
}

/* ── Engagement chart (live) ── */

function EngagementChartCard({ campaignId }: { campaignId: string }) {
  const { data, isLoading } = useEngagement({ campaign_id: campaignId, days: 14 });

  return (
    <section
      style={{
        backgroundColor: 'var(--card-bg)',
        border: '1px solid var(--card-border)',
        borderRadius: 'var(--card-radius)',
        boxShadow: 'var(--card-shadow)',
        padding: 'var(--card-padding)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-4)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h2
          style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-lg)',
            fontWeight: 'var(--font-weight-semibold)',
            color: 'var(--color-text-brand)',
            letterSpacing: 'var(--tracking-tight)',
          }}
        >
          Engagement · last 14 days
        </h2>
        <ChartLegend />
      </div>
      {data && data.length > 0
        ? <EngagementLineChart points={data} />
        : <EmptyChart message={isLoading ? 'Loading…' : 'No activity yet for this campaign.'} />
      }
    </section>
  );
}

function EmptyChart({ message }: { message: string }) {
  return (
    <div style={{
      height: 220,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: 'var(--font-sans)',
      fontSize: 'var(--text-sm)',
      color: 'var(--color-text-muted)',
    }}>{message}</div>
  );
}

function ChartLegend() {
  const items = [
    { label: 'Sends', color: 'var(--color-text-muted)' },
    { label: 'Opens', color: 'var(--color-brand-blue)' },
    { label: 'Replies', color: 'var(--color-brand-indigo)' },
  ];
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
      {items.map((it) => (
        <span
          key={it.label}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            fontSize: 'var(--text-xs)',
            fontWeight: 'var(--font-weight-medium)',
            color: 'var(--color-text-muted)',
          }}
        >
          <span style={{ width: '10px', height: '10px', borderRadius: 'var(--radius-full)', backgroundColor: it.color }} />
          {it.label}
        </span>
      ))}
    </div>
  );
}

function EngagementLineChart({ points }: { points: DailyEngagementPoint[] }) {
  const width = 720;
  const height = 220;
  const padding = { top: 16, right: 16, bottom: 32, left: 36 };
  const innerW = width - padding.left - padding.right;
  const innerH = height - padding.top - padding.bottom;

  const series = [
    { values: points.map((p) => p.sent), color: 'var(--color-text-muted)' },
    { values: points.map((p) => p.opened), color: 'var(--color-brand-blue)' },
    { values: points.map((p) => p.replied), color: 'var(--color-brand-indigo)' },
  ];
  const labels = points.map((p) => p.date.slice(5)); // MM-DD

  const allValues = series.flatMap((s) => s.values);
  // Default max = 1 when no data so the chart renders without div-by-zero.
  const max = Math.max(1, ...allValues);
  const step = labels.length > 1 ? innerW / (labels.length - 1) : 0;

  const toPath = (values: number[]) =>
    values
      .map((v, i) => {
        const x = padding.left + i * step;
        const y = padding.top + innerH - (v / max) * innerH;
        return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
      })
      .join(' ');

  const yTicks = 4;
  const tickValues = Array.from({ length: yTicks + 1 }, (_, i) => Math.round((max / yTicks) * i));

  return (
    <div style={{ width: '100%', overflow: 'hidden' }}>
      <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" width="100%" height={height} style={{ display: 'block' }}>
        {tickValues.map((tv, i) => {
          const y = padding.top + innerH - (tv / max) * innerH;
          return (
            <g key={i}>
              <line
                x1={padding.left}
                x2={width - padding.right}
                y1={y}
                y2={y}
                stroke="var(--color-border-default)"
                strokeWidth={1}
                strokeDasharray={i === yTicks ? '0' : '2 4'}
              />
              <text x={padding.left - 8} y={y + 4} textAnchor="end" fontSize="10" fontFamily="var(--font-sans)" fill="var(--color-text-subtle)">
                {tv}
              </text>
            </g>
          );
        })}

        {series.map((s, i) => (
          <path key={i} d={toPath(s.values)} fill="none" stroke={s.color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
        ))}

        {series.map((s, i) => {
          const last = s.values.length - 1;
          if (last < 0) return null;
          const x = padding.left + last * step;
          const y = padding.top + innerH - (s.values[last] / max) * innerH;
          return <circle key={i} cx={x} cy={y} r={3.5} fill={s.color} stroke="var(--color-surface-white)" strokeWidth={1.5} />;
        })}

        {labels.map((label, i) => {
          if (i % 2 !== 0 && i !== labels.length - 1) return null;
          const x = padding.left + i * step;
          return (
            <text key={i} x={x} y={height - 10} textAnchor="middle" fontSize="10" fontFamily="var(--font-sans)" fill="var(--color-text-subtle)">
              {label}
            </text>
          );
        })}
      </svg>
    </div>
  );
}

/* ── Right rail: activity feed (live) + inbox shortcut ── */

const EVENT_STYLE: Record<ActivityEventType, { bg: string; fg: string; verb: string; actor: string }> = {
  SENT:          { bg: 'var(--color-blue-50)',     fg: 'var(--color-brand-blue)',   verb: 'sent', actor: 'System' },
  DELIVERED:     { bg: 'var(--color-success-bg)',  fg: 'var(--color-success)',      verb: 'was delivered', actor: 'Postmark' },
  OPENED:        { bg: 'var(--color-blue-50)',     fg: 'var(--color-brand-blue)',   verb: 'opened the email', actor: 'Recipient' },
  CLICKED:       { bg: 'var(--color-indigo-50)',   fg: 'var(--color-brand-indigo)', verb: 'clicked a link', actor: 'Recipient' },
  REPLIED:       { bg: 'var(--color-indigo-50)',   fg: 'var(--color-brand-indigo)', verb: 'replied', actor: 'Recipient' },
  BOUNCED:       { bg: 'var(--color-warning-bg)',  fg: 'var(--color-warning)',      verb: 'bounced', actor: 'Recipient' },
  UNSUBSCRIBED:  { bg: 'var(--color-warning-bg)',  fg: 'var(--color-warning)',      verb: 'unsubscribed', actor: 'Recipient' },
  SPAM_REPORTED: { bg: 'var(--color-warning-bg)',  fg: 'var(--color-warning)',      verb: 'reported as spam', actor: 'Recipient' },
};

function ActivityFeedPanel({ campaignId }: { campaignId: string }) {
  const { data, isLoading } = useActivityFeed({ campaign_id: campaignId, limit: 25 });
  const events = data ?? [];

  return (
    <section
      style={{
        backgroundColor: 'var(--card-bg)',
        border: '1px solid var(--card-border)',
        borderRadius: 'var(--card-radius)',
        boxShadow: 'var(--card-shadow)',
        overflow: 'hidden',
      }}
    >
      <PanelHeader title="Recent activity" linkLabel="View all" linkTo={`/campaigns/${campaignId}/report`} />
      {isLoading && events.length === 0 && (
        <div style={{ padding: 'var(--space-4)', color: 'var(--color-text-muted)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)' }}>Loading…</div>
      )}
      {!isLoading && events.length === 0 && (
        <div style={{ padding: 'var(--space-4)', color: 'var(--color-text-muted)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)' }}>
          No activity yet: sends and replies will appear here as they happen.
        </div>
      )}
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {events.map((evt, i) => {
          const style = EVENT_STYLE[evt.event_type];
          const subject = (evt.contact_email ?? style.actor);
          return (
            <li
              key={evt.id}
              style={{
                padding: 'var(--space-3) var(--space-4)',
                borderTop: i === 0 ? 'none' : '1px solid var(--color-border-default)',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 'var(--space-3)',
              }}
            >
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '24px',
                  height: '24px',
                  borderRadius: 'var(--radius-full)',
                  backgroundColor: style.bg,
                  color: style.fg,
                  flexShrink: 0,
                  marginTop: '2px',
                }}
              >
                <EventIcon eventType={evt.event_type} />
              </span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
                <p style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', lineHeight: 'var(--leading-snug)' }}>
                  <span style={{ fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)' }}>{subject}</span>{' '}
                  {style.verb}.
                </p>
                <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                  {formatRelative(evt.occurred_at)}
                </span>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function EventIcon({ eventType }: { eventType: ActivityEventType }) {
  switch (eventType) {
    case 'SENT':         return <IconSend size={11} />;
    case 'OPENED':       return <IconCheck size={11} />;
    case 'CLICKED':      return <IconCheck size={11} />;
    case 'REPLIED':      return <IconReply size={11} />;
    case 'BOUNCED':      return <IconAlert size={11} />;
    case 'UNSUBSCRIBED': return <IconAlert size={11} />;
    case 'SPAM_REPORTED':return <IconAlert size={11} />;
    case 'DELIVERED':    return <IconCheck size={11} />;
    default:             return <IconCheck size={11} />;
  }
}

function formatRelative(iso: string): string {
  const t = new Date(iso).getTime();
  const diffSec = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (diffSec < 60) return 'just now';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 24 * 3600) return `${Math.floor(diffSec / 3600)}h ago`;
  if (diffSec < 7 * 24 * 3600) return `${Math.floor(diffSec / (24 * 3600))}d ago`;
  return new Date(iso).toLocaleDateString();
}

function InboxShortcut({ campaignId }: { campaignId: string }) {
  // Scope counts to this campaign so the shortcut reflects "replies on
  // THIS campaign" rather than the whole inbox.
  const { data: unread } = useConversations({ unread: true, campaign_id: campaignId });
  const { data: all } = useConversations({ campaign_id: campaignId });
  const unreadCount = unread?.data?.length ?? 0;
  const totalCount = all?.data?.length ?? 0;

  return (
    <section
      style={{
        backgroundColor: 'var(--card-bg)',
        border: '1px solid var(--card-border)',
        borderRadius: 'var(--card-radius)',
        boxShadow: 'var(--card-shadow)',
        padding: 'var(--card-padding)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-4)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h3 style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-lg)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-brand)', letterSpacing: 'var(--tracking-tight)' }}>
          Inbox
        </h3>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '32px',
            height: '32px',
            borderRadius: 'var(--radius-input)',
            backgroundColor: 'var(--color-blue-50)',
            color: 'var(--color-brand-blue)',
          }}
        >
          <IconInbox size={16} />
        </span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 'var(--space-3)' }}>
        <InboxCount label="Unread" value={unreadCount} highlight />
        <InboxCount label="Threads" value={totalCount} />
      </div>

      <a
        href="/inbox"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 'var(--space-2)',
          height: 'var(--btn-height)',
          borderRadius: 'var(--btn-radius)',
          backgroundColor: 'var(--color-action-primary)',
          color: 'var(--color-action-primary-text)',
          fontSize: 'var(--btn-font-size)',
          fontWeight: 'var(--btn-font-weight)',
          fontFamily: 'var(--font-sans)',
          textDecoration: 'none',
          transition: 'background-color var(--transition-base)',
        }}
        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-action-primary-hover)')}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-action-primary)')}
      >
        Open inbox
        <IconArrowRight size={14} />
      </a>
    </section>
  );
}

function InboxCount({ label, value, highlight = false }: { label: string; value: number; highlight?: boolean }) {
  return (
    <div
      style={{
        padding: 'var(--space-3) var(--space-4)',
        borderRadius: 'var(--radius-input)',
        backgroundColor: highlight ? 'var(--color-blue-50)' : 'var(--color-surface-muted)',
        border: `1px solid ${highlight ? 'var(--color-blue-100)' : 'var(--color-border-default)'}`,
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
      }}
    >
      <span
        style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--text-xs)',
          fontWeight: 'var(--font-weight-semibold)',
          color: highlight ? 'var(--color-brand-blue)' : 'var(--color-text-muted)',
          textTransform: 'uppercase',
          letterSpacing: 'var(--tracking-caps)',
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--text-4xl)',
          fontWeight: 'var(--font-weight-bold)',
          color: highlight ? 'var(--color-brand-blue)' : 'var(--color-text-primary)',
          letterSpacing: 'var(--tracking-tight)',
          lineHeight: 'var(--leading-tight)',
        }}
      >
        {value}
      </span>
    </div>
  );
}

/* ── Shared bits ── */

function PanelHeader({ title, linkLabel, linkTo }: { title: string; linkLabel: string; linkTo: string }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: 'var(--space-4) var(--space-5)',
        borderBottom: '1px solid var(--color-border-default)',
      }}
    >
      <h2 style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-lg)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-brand)', letterSpacing: 'var(--tracking-tight)' }}>
        {title}
      </h2>
      <a
        href={linkTo}
        style={{
          fontSize: 'var(--text-sm)',
          fontWeight: 'var(--font-weight-semibold)',
          color: 'var(--color-text-link)',
          textDecoration: 'none',
        }}
      >
        {linkLabel} →
      </a>
    </div>
  );
}


function PrimaryButton({ children, onClick, disabled }: { children: ReactNode; onClick?: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 'var(--btn-height)',
        padding: '0 var(--btn-padding-x)',
        borderRadius: 'var(--btn-radius)',
        backgroundColor: 'var(--color-action-primary)',
        color: 'var(--color-action-primary-text)',
        border: 'none',
        fontSize: 'var(--btn-font-size)',
        fontWeight: 'var(--btn-font-weight)',
        fontFamily: 'var(--font-sans)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </button>
  );
}

function SecondaryButton({ children, onClick, disabled }: { children: ReactNode; onClick?: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 'var(--btn-height)',
        padding: '0 var(--btn-padding-x)',
        borderRadius: 'var(--btn-radius)',
        backgroundColor: 'var(--color-action-secondary)',
        color: 'var(--color-action-secondary-text)',
        border: '1px solid var(--color-action-secondary-border)',
        fontSize: 'var(--btn-font-size)',
        fontWeight: 'var(--btn-font-weight)',
        fontFamily: 'var(--font-sans)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'background-color var(--transition-base)',
        whiteSpace: 'nowrap',
      }}
      onMouseEnter={(e) => {
        if (!disabled) e.currentTarget.style.backgroundColor = 'var(--color-action-secondary-hover)';
      }}
      onMouseLeave={(e) => {
        if (!disabled) e.currentTarget.style.backgroundColor = 'var(--color-action-secondary)';
      }}
    >
      {children}
    </button>
  );
}

/* ── Icons ── */

function IconChevronRight({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

function IconArrowRight({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );
}

function IconPause({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="6" y="4" width="4" height="16" />
      <rect x="14" y="4" width="4" height="16" />
    </svg>
  );
}

function IconWorkflow({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="6" height="6" rx="1" />
      <rect x="15" y="3" width="6" height="6" rx="1" />
      <rect x="9" y="15" width="6" height="6" rx="1" />
      <path d="M6 9v3a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V9" />
    </svg>
  );
}

function IconBookmark({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
    </svg>
  );
}

function IconBarChart({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="20" x2="18" y2="10" />
      <line x1="12" y1="20" x2="12" y2="4" />
      <line x1="6" y1="20" x2="6" y2="14" />
    </svg>
  );
}

function IconCalendar({ size = 12 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}

function IconLayers({ size = 12 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 2 7 12 12 22 7 12 2" />
      <polyline points="2 17 12 22 22 17" />
      <polyline points="2 12 12 17 22 12" />
    </svg>
  );
}

function IconStop({ size = 12 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
    </svg>
  );
}

function IconMail({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22 6 12 13 2 6" />
    </svg>
  );
}

function IconClock({ size = 11 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

function IconBranch({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="6" y1="3" x2="6" y2="15" />
      <circle cx="18" cy="6" r="3" />
      <circle cx="6" cy="18" r="3" />
      <path d="M18 9a9 9 0 0 1-9 9" />
    </svg>
  );
}

function IconCheckSquare({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 11 12 14 22 4" />
      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
    </svg>
  );
}

function IconLogOut({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}

function IconCheck({ size = 11 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function IconSend({ size = 11 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  );
}

function IconReply({ size = 11 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 17 4 12 9 7" />
      <path d="M20 18v-2a4 4 0 0 0-4-4H4" />
    </svg>
  );
}

function IconAlert({ size = 11 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

function IconInbox({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
      <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
    </svg>
  );
}
